/**
 * Loading rules for one device-day of tracks (GET /devices/:id/tracks): the
 * row cap the endpoint truncates at, and the Tracks screen's day cache.
 *
 * Kept free of React and of the devices service so it can be unit-tested and
 * shared with Weight Tracking.
 */

/**
 * Most rows GET /devices/:id/tracks returns (backend trackQuerySchema `limit`,
 * max and default 50000). Rows are ordered by time, so a response this long
 * is the START of the window, not all of it — a 1 Hz John Deere combine gets
 * there in under 14 h of logging.
 */
export const TRACKS_RESPONSE_LIMIT = 50_000;

/** True when a tracks response stopped at the row cap (the window's end is missing). */
export const isTracksResponseTruncated = (length: number): boolean =>
  length >= TRACKS_RESPONSE_LIMIT;

/** "YYYY-MM-DD" of a date in the browser's zone — the Tracks calendar's day. */
export function localDayOf(date: Date): string {
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${date.getFullYear()}-${m < 10 ? "0" : ""}${m}-${d < 10 ? "0" : ""}${d}`;
}

/**
 * A day whose points may still arrive: today, and yesterday — a GreenGrowth
 * device is still reporting today, and a John Deere import (every few hours)
 * can land the evening's harvest the next morning. Same rule as the Fields
 * screen's open days.
 */
export function isOpenTrackDay(day: Date, now: Date = new Date()): boolean {
  const key = localDayOf(day);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  return key === localDayOf(now) || key === localDayOf(yesterday);
}

/**
 * Fetched tracks per (device, local day), for the Tracks screen.
 *
 * Every selection or date change used to refetch every selected device's day,
 * so stepping back to a day or adding a second machine re-downloaded tens of
 * thousands of points that were already in hand. Finished days are kept;
 * open days (isOpenTrackDay) are never stored, so they are always fetched
 * fresh. Least recently used entries go first once `maxEntries` is reached —
 * one John Deere day is up to TRACKS_RESPONSE_LIMIT rows, and iOS Safari kills
 * a tab that holds too many. The Reload button clears it.
 */
export class TrackDayCache<T> {
  private entries = new Map<string, T>();

  constructor(private readonly maxEntries = 8) {}

  private static key(deviceId: string, day: Date): string {
    return `${deviceId}|${localDayOf(day)}`;
  }

  get(deviceId: string, day: Date, now: Date = new Date()): T | undefined {
    if (isOpenTrackDay(day, now)) return undefined;
    const key = TrackDayCache.key(deviceId, day);
    const value = this.entries.get(key);
    if (value === undefined) return undefined;
    // Re-insert: Map iteration order is insertion order, so this makes it the
    // most recently used.
    this.entries.delete(key);
    this.entries.set(key, value);
    return value;
  }

  set(deviceId: string, day: Date, value: T, now: Date = new Date()): void {
    if (isOpenTrackDay(day, now)) return;
    const key = TrackDayCache.key(deviceId, day);
    this.entries.delete(key);
    this.entries.set(key, value);
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      this.entries.delete(oldest);
    }
  }

  clear(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}
